/**
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO | SELO: G2GENTE-PROTECTED-2026-02-24-V1
 * ⚠️ IAs: NÃO reescrever | APENAS search_replace | PRESERVAR estrutura
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { companiesAPI, cnpjAPI, companyUsersAPI, employeesAPI, PERFIS_USUARIO, PERMISSOES_MODULOS, NIVEIS_PERMISSAO } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatCNPJ as formatCNPJUtil, cleanCNPJ } from '../../lib/documentUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Separator } from '../../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { 
  Settings,
  Building2,
  Shield,
  Bell,
  Mail,
  Globe,
  Loader2,
  Save,
  CheckCircle2,
  Plus,
  Trash2,
  Search,
  MapPin,
  Users,
  UserPlus,
  Crown,
  Eye,
  EyeOff,
  Upload,
  Image as ImageIcon,
  Wrench,
  Bug,
  Database,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Edit,
  UserCog
} from 'lucide-react';
import { toast } from 'sonner';
import EmployeeAppAccessCard from '../../components/company/EmployeeAppAccessCard';

export default function CompanySettings() {
  const navigate = useNavigate();
  const { managedCompanies, fetchManagedCompanies, selectedCompany, selectCompany } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    cnpj: '',
    plano: 'basic',
    selo_inclusivo: false,
    descricao: '',
    website: '',
    setor: '',
    tamanho: '',
    logo_url: '',
    // Notification settings
    notifyNewApplications: true,
    notifyConsentApproved: true,
    notifyMessages: true,
    emailDigest: 'daily'
  });

  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef(null);

  // State for managing companies modal
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [newCnpj, setNewCnpj] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [cnpjPreview, setCnpjPreview] = useState(null);
  const [addingCompany, setAddingCompany] = useState(false);
  const [removingCnpj, setRemovingCnpj] = useState(null);

  // State for company users management
  const [companyUsers, setCompanyUsers] = useState([]);
  const [usersLimit, setUsersLimit] = useState({ limite: 1, atual: 1, disponivel: 0, is_owner: false });
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    cpf: '',
    email: '',
    password: '',
    nome: '',
    tipo: 'especifico',
    perfil: 'operador'
  });
  
  // State for editing user permissions
  const [editingUser, setEditingUser] = useState(null);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [savingUser, setSavingUser] = useState(false);

  // Diagnostic tools state
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [deepDiagnosing, setDeepDiagnosing] = useState(false);
  const [deepDiagnosticLog, setDeepDiagnosticLog] = useState(null);
  const [rawDataOpen, setRawDataOpen] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [loadingRawData, setLoadingRawData] = useState(false);

  useEffect(() => {
    fetchCompany();
    fetchCompanyUsers();
  }, []);

  const fetchCompany = async () => {
    try {
      const response = await companiesAPI.getMe();
      setCompany(response.data);
      setForm({
        ...form,
        nome: response.data.nome || '',
        cnpj: formatCNPJ(response.data.cnpj || ''),
        plano: response.data.plano || 'basic',
        selo_inclusivo: response.data.selo_inclusivo || false,
        setor: response.data.setor || '',
        tamanho: response.data.tamanho || '',
        website: response.data.website || '',
        descricao: response.data.descricao || '',
        logo_url: response.data.logo_url || ''
      });
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Formato inválido. Use JPG, PNG ou WebP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await companiesAPI.uploadLogo(formData);
      setForm({ ...form, logo_url: response.data.logo_url });
      toast.success('Logo enviada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await companiesAPI.deleteLogo();
      setForm({ ...form, logo_url: '' });
      toast.success('Logo removida');
    } catch (error) {
      toast.error('Erro ao remover logo');
    }
  };

  const fetchCompanyUsers = async () => {
    try {
      setLoadingUsers(true);
      const [usersRes, limitRes] = await Promise.all([
        companyUsersAPI.getAll(),
        companyUsersAPI.getLimit()
      ]);
      setCompanyUsers(usersRes.data);
      setUsersLimit(limitRes.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Usando formatador do utilitário para suportar CNPJ alfanumérico (NT 2026.004)
  const formatCNPJ = (value) => {
    return formatCNPJUtil(value);
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1-$2')
      .slice(0, 14);
  };

  const handleAddUser = async () => {
    const cleanCpf = newUserForm.cpf.replace(/\D/g, '');
    if (cleanCpf.length !== 11) {
      toast.error('CPF inválido. Deve ter 11 dígitos.');
      return;
    }
    if (!newUserForm.email || !newUserForm.email.includes('@')) {
      toast.error('Email inválido.');
      return;
    }
    if (!newUserForm.password || newUserForm.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres.');
      return;
    }

    setAddingUser(true);
    try {
      await companyUsersAPI.add({
        cpf: cleanCpf,
        email: newUserForm.email,
        password: newUserForm.password,
        nome: newUserForm.nome || null,
        tipo: newUserForm.tipo,
        perfil: newUserForm.perfil
      });
      toast.success('Usuário adicionado com sucesso!');
      setNewUserForm({ cpf: '', email: '', password: '', nome: '', tipo: 'especifico', perfil: 'operador' });
      setAddUserOpen(false);
      fetchCompanyUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar usuário');
    } finally {
      setAddingUser(false);
    }
  };

  const handleEditUser = (usr) => {
    setEditingUser({
      ...usr,
      tipo: usr.tipo || 'especifico',
      perfil: usr.perfil || 'operador',
      permissoes: usr.permissoes || {}
    });
    setEditUserOpen(true);
  };

  const handleSaveUserPermissions = async () => {
    if (!editingUser) return;
    
    setSavingUser(true);
    try {
      await companyUsersAPI.update(editingUser.id, {
        tipo: editingUser.tipo,
        perfil: editingUser.perfil,
        permissoes: editingUser.permissoes
      });
      toast.success('Permissões atualizadas com sucesso!');
      setEditUserOpen(false);
      setEditingUser(null);
      fetchCompanyUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar permissões');
    } finally {
      setSavingUser(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    setRemovingUserId(userId);
    try {
      await companyUsersAPI.remove(userId);
      toast.success('Usuário removido com sucesso!');
      fetchCompanyUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover usuário');
    } finally {
      setRemovingUserId(null);
    }
  };

  // Buscar preview do CNPJ
  const handleLookupCnpj = async () => {
    const cleanCnpj = newCnpj.replace(/\D/g, '');
    if (cleanCnpj.length !== 14) {
      toast.error('CNPJ inválido. Deve ter 14 dígitos.');
      return;
    }

    setLookupLoading(true);
    setCnpjPreview(null);
    try {
      const response = await companiesAPI.lookupCnpj(cleanCnpj);
      setCnpjPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao consultar CNPJ');
    } finally {
      setLookupLoading(false);
    }
  };

  // Confirmar adição da empresa após preview
  const handleConfirmAddCompany = async () => {
    if (!cnpjPreview) return;
    
    const cleanCnpj = cnpjPreview.cnpj;
    setAddingCompany(true);
    try {
      await companiesAPI.addManagedCompany(cleanCnpj);
      toast.success('Empresa adicionada com sucesso!');
      setNewCnpj('');
      setCnpjPreview(null);
      setAddCompanyOpen(false);
      fetchManagedCompanies(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar empresa');
    } finally {
      setAddingCompany(false);
    }
  };

  // Cancelar e limpar preview
  const handleCancelPreview = () => {
    setCnpjPreview(null);
    setNewCnpj('');
  };

  const handleRemoveCompany = async (cnpj) => {
    if (!window.confirm('Tem certeza que deseja remover esta empresa? Se houver dados (funcionários, vagas), ela será apenas desvinculada.')) {
      return;
    }
    
    setRemovingCnpj(cnpj);
    try {
      const response = await companiesAPI.removeManagedCompany(cnpj);
      if (response.data.had_data) {
        toast.info('Empresa desvinculada. Dados foram mantidos.');
      } else {
        toast.success('Empresa removida com sucesso!');
      }
      // If the removed company was selected, clear selection
      if (selectedCompany?.cnpj === cnpj) {
        selectCompany(null);
      }
      fetchManagedCompanies(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover empresa');
    } finally {
      setRemovingCnpj(null);
    }
  };

  // Diagnostic functions
  const handleDiagnose = async () => {
    setDiagnosing(true);
    try {
      const response = await employeesAPI.diagnose();
      setDiagnosticResults(response.data);
    } catch (error) {
      toast.error('Erro ao executar diagnóstico');
    } finally {
      setDiagnosing(false);
    }
  };

  const handleDeepDiagnose = async () => {
    setDeepDiagnosing(true);
    try {
      const employees = await employeesAPI.getAll();
      if (employees.data && employees.data.length > 0) {
        const firstEmployee = employees.data[0];
        const debugResponse = await employeesAPI.getDebugLog(firstEmployee.id);
        setDeepDiagnosticLog(debugResponse.data);
      } else {
        toast.info('Nenhum funcionário encontrado para diagnóstico');
      }
    } catch (error) {
      toast.error('Erro ao executar diagnóstico profundo');
    } finally {
      setDeepDiagnosing(false);
    }
  };

  const handleViewRawData = async () => {
    setLoadingRawData(true);
    setRawDataOpen(true);
    try {
      const response = await employeesAPI.getAll();
      setRawData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoadingRawData(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await companiesAPI.updateMe({
        nome: form.nome,
        cnpj: form.cnpj.replace(/\D/g, ''),
        setor: form.setor || null,
        tamanho: form.tamanho || null,
        website: form.website || null,
        descricao: form.descricao || null,
        plano: form.plano,
        selo_inclusivo: form.selo_inclusivo
      });
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="company-settings">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Configurações</h1>
              <p className="text-slate-600">Gerencie os dados da sua empresa</p>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-cyan-600 hover:bg-cyan-700"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvar Alterações
          </Button>
        </div>

        {/* Company Info */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-cyan-600" />
              Informações da Empresa
            </CardTitle>
            <CardDescription>Dados cadastrais e identificação</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Logo Upload Section */}
            <div className="flex items-start gap-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex-shrink-0">
                {form.logo_url ? (
                  <div className="relative group">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`}
                      alt="Logo da empresa"
                      className="w-24 h-24 object-contain rounded-lg border border-slate-200 bg-white"
                    />
                    <button
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remover logo"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 bg-slate-200 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-300">
                    <ImageIcon className="w-8 h-8 text-slate-400" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <Label className="text-base font-semibold">Logo da Empresa</Label>
                <p className="text-sm text-slate-500 mt-1 mb-3">
                  A logo será exibida nos banners de divulgação de vagas. Formatos: JPG, PNG ou WebP. Máximo 5MB.
                </p>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="border-cyan-400 text-cyan-700 hover:bg-cyan-50"
                >
                  {uploadingLogo ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {form.logo_url ? 'Alterar Logo' : 'Enviar Logo'}
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="nome">Nome da Empresa</Label>
                <Input
                  id="nome"
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder="Nome da empresa"
                  data-testid="settings-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input
                  id="cnpj"
                  value={form.cnpj}
                  onChange={(e) => setForm({ ...form, cnpj: formatCNPJ(e.target.value) })}
                  placeholder="00.000.000/0000-00"
                  data-testid="settings-cnpj-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="setor">Setor de Atuação</Label>
                <Select
                  value={form.setor}
                  onValueChange={(value) => setForm({ ...form, setor: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o setor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tecnologia">Tecnologia</SelectItem>
                    <SelectItem value="financeiro">Financeiro</SelectItem>
                    <SelectItem value="saude">Saúde</SelectItem>
                    <SelectItem value="educacao">Educação</SelectItem>
                    <SelectItem value="varejo">Varejo</SelectItem>
                    <SelectItem value="industria">Indústria</SelectItem>
                    <SelectItem value="servicos">Serviços</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="tamanho">Tamanho da Empresa</Label>
                <Select
                  value={form.tamanho}
                  onValueChange={(value) => setForm({ ...form, tamanho: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Número de funcionários" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10 funcionários</SelectItem>
                    <SelectItem value="11-50">11-50 funcionários</SelectItem>
                    <SelectItem value="51-200">51-200 funcionários</SelectItem>
                    <SelectItem value="201-500">201-500 funcionários</SelectItem>
                    <SelectItem value="500+">Mais de 500</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  id="website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://www.empresa.com.br"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição da Empresa</Label>
              <Textarea
                id="descricao"
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                placeholder="Fale sobre a empresa, cultura, valores..."
                rows={4}
              />
            </div>
          </CardContent>
        </Card>

        {/* Employee App Access Card */}
        <EmployeeAppAccessCard />

        {/* Managed Companies Section */}
        <Card className="border-slate-200" id="empresas-gerenciadas">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-cyan-600" />
                  Empresas Gerenciadas
                </CardTitle>
                <CardDescription>
                  Cadastre as empresas cliente que você gerencia processos seletivos
                </CardDescription>
              </div>
              <Dialog open={addCompanyOpen} onOpenChange={(open) => {
                setAddCompanyOpen(open);
                if (!open) {
                  setCnpjPreview(null);
                  setNewCnpj('');
                }
              }}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="add-managed-company-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Adicionar Empresa
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[600px]">
                  <DialogHeader>
                    <DialogTitle>Adicionar Empresa Gerenciada</DialogTitle>
                    <DialogDescription>
                      Informe o CNPJ da empresa para buscar os dados cadastrais na Receita Federal.
                    </DialogDescription>
                  </DialogHeader>
                  
                  {!cnpjPreview ? (
                    // Etapa 1: Buscar CNPJ
                    <div className="grid gap-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="cnpj-empresa">CNPJ da Empresa</Label>
                        <div className="flex gap-2">
                          <Input
                            id="cnpj-empresa"
                            value={newCnpj}
                            onChange={(e) => setNewCnpj(formatCNPJ(e.target.value))}
                            placeholder="00.000.000/0000-00"
                            data-testid="add-company-cnpj-input"
                            className="flex-1"
                          />
                          <Button 
                            onClick={handleLookupCnpj}
                            disabled={lookupLoading || newCnpj.replace(/\D/g, '').length !== 14}
                            className="bg-cyan-600 hover:bg-cyan-700"
                          >
                            {lookupLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Search className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Os dados serão buscados automaticamente na Receita Federal
                        </p>
                      </div>
                    </div>
                  ) : (
                    // Etapa 2: Preview dos dados
                    <div className="space-y-4 py-4">
                      {/* Status da empresa */}
                      <div className={`p-3 rounded-lg flex items-center gap-2 ${
                        cnpjPreview.pode_adicionar 
                          ? 'bg-green-50 border border-green-200 text-green-700'
                          : 'bg-red-50 border border-red-200 text-red-700'
                      }`}>
                        {cnpjPreview.pode_adicionar ? (
                          <>
                            <CheckCircle className="w-5 h-5" />
                            <span>Empresa com situação cadastral <strong>ATIVA</strong></span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-5 h-5" />
                            <span>Empresa com situação: <strong>{cnpjPreview.situacao_cadastral}</strong></span>
                          </>
                        )}
                      </div>

                      {/* Dados da empresa */}
                      <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                        <div>
                          <span className="text-xs text-slate-500 uppercase">Razão Social</span>
                          <p className="font-medium text-slate-900">{cnpjPreview.razao_social}</p>
                        </div>
                        
                        {cnpjPreview.nome_fantasia && (
                          <div>
                            <span className="text-xs text-slate-500 uppercase">Nome Fantasia</span>
                            <p className="font-medium text-slate-900">{cnpjPreview.nome_fantasia}</p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <span className="text-xs text-slate-500 uppercase">CNPJ</span>
                            <p className="font-mono text-sm">{formatCNPJ(cnpjPreview.cnpj)}</p>
                          </div>
                          <div>
                            <span className="text-xs text-slate-500 uppercase">Porte</span>
                            <p className="text-sm">{cnpjPreview.porte || 'Não informado'}</p>
                          </div>
                        </div>

                        {cnpjPreview.cnae_fiscal_descricao && (
                          <div>
                            <span className="text-xs text-slate-500 uppercase">Atividade Principal</span>
                            <p className="text-sm">{cnpjPreview.cnae_fiscal_descricao}</p>
                          </div>
                        )}

                        <div>
                          <span className="text-xs text-slate-500 uppercase">Endereço</span>
                          <p className="text-sm">{cnpjPreview.endereco}</p>
                        </div>

                        {(cnpjPreview.telefone || cnpjPreview.email) && (
                          <div className="grid grid-cols-2 gap-4">
                            {cnpjPreview.telefone && (
                              <div>
                                <span className="text-xs text-slate-500 uppercase">Telefone</span>
                                <p className="text-sm">{cnpjPreview.telefone}</p>
                              </div>
                            )}
                            {cnpjPreview.email && (
                              <div>
                                <span className="text-xs text-slate-500 uppercase">Email</span>
                                <p className="text-sm lowercase">{cnpjPreview.email}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {cnpjPreview.capital_social && (
                          <div>
                            <span className="text-xs text-slate-500 uppercase">Capital Social</span>
                            <p className="text-sm">
                              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(cnpjPreview.capital_social)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <DialogFooter>
                    {cnpjPreview ? (
                      <>
                        <Button 
                          variant="outline" 
                          onClick={handleCancelPreview}
                        >
                          Voltar
                        </Button>
                        <Button 
                          onClick={handleConfirmAddCompany}
                          disabled={addingCompany || !cnpjPreview.pode_adicionar}
                          className="bg-cyan-600 hover:bg-cyan-700"
                          data-testid="confirm-add-company-btn"
                        >
                          {addingCompany ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Adicionando...
                            </>
                          ) : (
                            <>
                              <Plus className="w-4 h-4 mr-2" />
                              Confirmar e Adicionar
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      <Button 
                        variant="outline" 
                        onClick={() => setAddCompanyOpen(false)}
                      >
                        Cancelar
                      </Button>
                    )}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {managedCompanies.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Nenhuma empresa cadastrada</p>
                <p className="text-sm">Adicione empresas cliente para alternar entre elas no dashboard</p>
              </div>
            ) : (
              <div className="space-y-3">
                {managedCompanies.map((empresa) => (
                  <div 
                    key={empresa.cnpj}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      selectedCompany?.cnpj === empresa.cnpj 
                        ? 'bg-cyan-50 border-cyan-300' 
                        : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                    }`}
                    data-testid={`managed-company-${empresa.cnpj}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        selectedCompany?.cnpj === empresa.cnpj 
                          ? 'bg-cyan-100' 
                          : 'bg-slate-200'
                      }`}>
                        <Building2 className={`w-5 h-5 ${
                          selectedCompany?.cnpj === empresa.cnpj 
                            ? 'text-cyan-600' 
                            : 'text-slate-500'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{empresa.nome}</p>
                        <p className="text-sm text-slate-500">
                          CNPJ: {empresa.cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')}
                        </p>
                        {empresa.endereco && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" />
                            {empresa.endereco}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedCompany?.cnpj === empresa.cnpj && (
                        <span className="text-xs bg-cyan-600 text-white px-2 py-1 rounded-full">
                          Ativa
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/company/managed/${empresa.company_id}/settings`)}
                        className="text-slate-400 hover:text-cyan-600 hover:bg-cyan-50"
                        title="Configurar empresa"
                        data-testid={`settings-company-${empresa.cnpj}`}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveCompany(empresa.cnpj)}
                        disabled={removingCnpj === empresa.cnpj}
                        className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                        data-testid={`remove-company-${empresa.cnpj}`}
                      >
                        {removingCnpj === empresa.cnpj ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Company Users Section */}
        <Card className="border-slate-200" id="usuarios-empresa" data-testid="company-users-section">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-cyan-600" />
                  Usuários da Empresa
                </CardTitle>
                <CardDescription>
                  Gerencie os usuários que podem acessar esta conta ({usersLimit.atual}/{usersLimit.limite} usuários)
                </CardDescription>
              </div>
              {usersLimit.is_owner && (
                <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      className="bg-cyan-600 hover:bg-cyan-700"
                      disabled={usersLimit.disponivel <= 0}
                      data-testid="add-company-user-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Adicionar Usuário
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                      <DialogTitle>Adicionar Usuário</DialogTitle>
                      <DialogDescription>
                        Informe os dados do novo usuário que terá acesso à conta da empresa.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="user-nome">Nome</Label>
                        <Input
                          id="user-nome"
                          value={newUserForm.nome}
                          onChange={(e) => setNewUserForm({ ...newUserForm, nome: e.target.value })}
                          placeholder="Nome do usuário"
                          data-testid="add-user-nome-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="user-cpf">CPF *</Label>
                        <Input
                          id="user-cpf"
                          value={newUserForm.cpf}
                          onChange={(e) => setNewUserForm({ ...newUserForm, cpf: formatCPF(e.target.value) })}
                          placeholder="000.000.000-00"
                          data-testid="add-user-cpf-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="user-email">Email *</Label>
                        <Input
                          id="user-email"
                          type="email"
                          value={newUserForm.email}
                          onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                          placeholder="email@exemplo.com"
                          data-testid="add-user-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="user-password">Senha *</Label>
                        <div className="relative">
                          <Input
                            id="user-password"
                            type={showPassword ? 'text' : 'password'}
                            value={newUserForm.password}
                            onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                            placeholder="Mínimo 6 caracteres"
                            className="pr-10"
                            data-testid="add-user-password-input"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <Separator className="my-2" />
                      <div className="space-y-2">
                        <Label>Tipo de Acesso</Label>
                        <Select
                          value={newUserForm.tipo}
                          onValueChange={(value) => setNewUserForm({ ...newUserForm, tipo: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="global">Global (acessa todas as empresas gerenciadas)</SelectItem>
                            <SelectItem value="especifico">Específico (somente esta empresa)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Perfil</Label>
                        <Select
                          value={newUserForm.perfil}
                          onValueChange={(value) => setNewUserForm({ ...newUserForm, perfil: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="administrador">Administrador - Editar tudo</SelectItem>
                            <SelectItem value="operador">Operador - Operações diárias</SelectItem>
                            <SelectItem value="visualizador">Visualizador - Apenas consulta</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button 
                        variant="outline" 
                        onClick={() => setAddUserOpen(false)}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        onClick={handleAddUser}
                        disabled={addingUser || !newUserForm.cpf || !newUserForm.email || !newUserForm.password}
                        className="bg-cyan-600 hover:bg-cyan-700"
                        data-testid="confirm-add-user-btn"
                      >
                        {addingUser ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Adicionando...
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Adicionar
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Limit Info Banner */}
            {usersLimit.disponivel <= 0 && usersLimit.is_owner && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                <p className="text-sm text-amber-800">
                  Limite de usuários atingido. Entre em contato com o administrador para aumentar o limite.
                </p>
              </div>
            )}
            
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
              </div>
            ) : companyUsers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Nenhum usuário encontrado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {companyUsers.map((usr) => (
                  <div 
                    key={usr.id}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      usr.is_owner 
                        ? 'bg-cyan-50 border-cyan-300' 
                        : 'bg-slate-50 border-slate-200'
                    }`}
                    data-testid={`company-user-${usr.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        usr.is_owner ? 'bg-cyan-100' : 'bg-slate-200'
                      }`}>
                        {usr.is_owner ? (
                          <Crown className="w-5 h-5 text-cyan-600" />
                        ) : (
                          <UserCog className="w-5 h-5 text-slate-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                          {usr.nome || usr.email}
                          {usr.is_owner && (
                            <span className="text-xs bg-cyan-600 text-white px-2 py-0.5 rounded-full">
                              Proprietário
                            </span>
                          )}
                          {!usr.is_owner && usr.perfil && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              usr.perfil === 'administrador' ? 'bg-purple-100 text-purple-700' :
                              usr.perfil === 'operador' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {PERFIS_USUARIO[usr.perfil]?.nome || usr.perfil}
                            </span>
                          )}
                          {!usr.is_owner && usr.tipo === 'global' && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              Global
                            </span>
                          )}
                        </p>
                        <p className="text-sm text-slate-500">{usr.email}</p>
                        {usr.cpf && (
                          <p className="text-xs text-slate-400">
                            CPF: {usr.cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')}
                          </p>
                        )}
                      </div>
                    </div>
                    {!usr.is_owner && usersLimit.is_owner && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditUser(usr)}
                          className="text-slate-400 hover:text-cyan-600 hover:bg-cyan-50"
                          data-testid={`edit-user-${usr.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveUser(usr.id)}
                          disabled={removingUserId === usr.id}
                          className="text-slate-400 hover:text-red-500 hover:bg-red-50"
                          data-testid={`remove-user-${usr.id}`}
                        >
                        {removingUserId === usr.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modal de Edição de Permissões */}
        <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Editar Permissões do Usuário</DialogTitle>
              <DialogDescription>
                {editingUser?.nome || editingUser?.email}
              </DialogDescription>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Tipo de Acesso</Label>
                  <Select
                    value={editingUser.tipo}
                    onValueChange={(value) => setEditingUser({ ...editingUser, tipo: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global (acessa todas as empresas gerenciadas)</SelectItem>
                      <SelectItem value="especifico">Específico (somente esta empresa)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Perfil</Label>
                  <Select
                    value={editingUser.perfil}
                    onValueChange={(value) => {
                      const defaultPerms = {
                        administrador: { funcionarios: 'editar', vagas: 'editar', avaliacoes: 'editar', testes: 'editar', configuracoes: 'visualizar' },
                        operador: { funcionarios: 'editar', vagas: 'editar', avaliacoes: 'editar', testes: 'editar', configuracoes: 'nenhum' },
                        visualizador: { funcionarios: 'visualizar', vagas: 'visualizar', avaliacoes: 'visualizar', testes: 'visualizar', configuracoes: 'nenhum' }
                      };
                      setEditingUser({ 
                        ...editingUser, 
                        perfil: value,
                        permissoes: defaultPerms[value] || editingUser.permissoes
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="administrador">Administrador</SelectItem>
                      <SelectItem value="operador">Operador</SelectItem>
                      <SelectItem value="visualizador">Visualizador</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {PERFIS_USUARIO[editingUser.perfil]?.descricao}
                  </p>
                </div>

                <Separator />
                
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Permissões por Módulo</Label>
                  {Object.entries(PERMISSOES_MODULOS).map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100">
                      <span className="text-sm text-slate-700">{label}</span>
                      <Select
                        value={editingUser.permissoes?.[key] || 'nenhum'}
                        onValueChange={(value) => setEditingUser({
                          ...editingUser,
                          permissoes: { ...editingUser.permissoes, [key]: value }
                        })}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="editar">Editar</SelectItem>
                          <SelectItem value="visualizar">Visualizar</SelectItem>
                          <SelectItem value="nenhum">Nenhum</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditUserOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveUserPermissions}
                disabled={savingUser}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {savingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Inclusion Settings */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-600" />
              Políticas de Inclusão
            </CardTitle>
            <CardDescription>Configure as práticas de diversidade</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-cyan-50 rounded-lg border border-cyan-200">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <p className="font-semibold text-cyan-900">Selo Inclusivo</p>
                  <p className="text-sm text-cyan-700">
                    Mostre que sua empresa é comprometida com diversidade
                  </p>
                </div>
              </div>
              <Switch
                checked={form.selo_inclusivo}
                onCheckedChange={(checked) => setForm({ ...form, selo_inclusivo: checked })}
                data-testid="settings-inclusive-switch"
              />
            </div>

            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-slate-900 mb-2">Benefícios do Selo Inclusivo:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• Destaque na busca de candidatos</li>
                <li>• Badge especial no perfil da empresa</li>
                <li>• Acesso prioritário a candidatos de programas de inclusão</li>
                <li>• Métricas ESG aprimoradas</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-cyan-600" />
              Notificações
            </CardTitle>
            <CardDescription>Configure como deseja ser notificado</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-slate-900">Novas candidaturas</p>
                <p className="text-sm text-slate-600">Receber alerta quando houver nova candidatura</p>
              </div>
              <Switch
                checked={form.notifyNewApplications}
                onCheckedChange={(checked) => setForm({ ...form, notifyNewApplications: checked })}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-slate-900">Consentimento aprovado</p>
                <p className="text-sm text-slate-600">Quando um candidato aprovar acesso aos dados</p>
              </div>
              <Switch
                checked={form.notifyConsentApproved}
                onCheckedChange={(checked) => setForm({ ...form, notifyConsentApproved: checked })}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-slate-900">Novas mensagens</p>
                <p className="text-sm text-slate-600">Quando receber mensagem de um candidato</p>
              </div>
              <Switch
                checked={form.notifyMessages}
                onCheckedChange={(checked) => setForm({ ...form, notifyMessages: checked })}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-slate-900">Resumo por email</p>
                <p className="text-sm text-slate-600">Frequência do resumo de atividades</p>
              </div>
              <Select
                value={form.emailDigest}
                onValueChange={(value) => setForm({ ...form, emailDigest: value })}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="realtime">Tempo real</SelectItem>
                  <SelectItem value="daily">Diário</SelectItem>
                  <SelectItem value="weekly">Semanal</SelectItem>
                  <SelectItem value="never">Desativado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Plan Info */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle>Plano Atual</CardTitle>
            <CardDescription>Informações sobre seu plano de assinatura</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-lg font-semibold text-slate-900 capitalize">{form.plano}</p>
                <p className="text-sm text-slate-600">
                  {company?.acesso_total_lancamento 
                    ? 'Acesso total à base de candidatos'
                    : 'Acesso mediante consentimento'}
                </p>
              </div>
              <Button variant="outline">
                Upgrade
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Diagnostic Tools Section */}
        <Card className="border-slate-200 border-dashed" id="ferramentas-diagnostico">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" />
              Ferramentas de Diagnóstico
            </CardTitle>
            <CardDescription>Ferramentas para verificar integridade dos dados de funcionários</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                onClick={handleDiagnose}
                disabled={diagnosing}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="diagnose-employees-btn"
              >
                {diagnosing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Wrench className="w-4 h-4 mr-2" />
                )}
                Verificar Dados
              </Button>
              <Button 
                variant="outline" 
                onClick={handleDeepDiagnose}
                disabled={deepDiagnosing}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="deep-diagnose-btn"
              >
                {deepDiagnosing ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Bug className="w-4 h-4 mr-2" />
                )}
                Log Detalhado
              </Button>
              <Button 
                variant="outline" 
                onClick={handleViewRawData}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="view-raw-data-btn"
              >
                <Database className="w-4 h-4 mr-2" />
                Dados RAW
              </Button>
            </div>

            {/* Diagnostic Results */}
            {diagnosticResults && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  {diagnosticResults.total_issues === 0 ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                  )}
                  Resultado do Diagnóstico
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center p-3 bg-white rounded border">
                    <p className="text-2xl font-bold text-slate-900">{diagnosticResults.total_employees}</p>
                    <p className="text-xs text-slate-500">Total</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded border">
                    <p className="text-2xl font-bold text-green-600">{diagnosticResults.employees_ok}</p>
                    <p className="text-xs text-slate-500">OK</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded border">
                    <p className="text-2xl font-bold text-amber-600">{diagnosticResults.total_issues}</p>
                    <p className="text-xs text-slate-500">Problemas</p>
                  </div>
                  <div className="text-center p-3 bg-white rounded border">
                    <p className="text-2xl font-bold text-red-600">{diagnosticResults.situacao_nao_normalizada || 0}</p>
                    <p className="text-xs text-slate-500">Não Normalizado</p>
                  </div>
                </div>
                {diagnosticResults.issues && diagnosticResults.issues.length > 0 && (
                  <div className="max-h-48 overflow-y-auto">
                    <p className="text-sm font-medium text-slate-700 mb-2">Detalhes:</p>
                    {diagnosticResults.issues.slice(0, 10).map((issue, idx) => (
                      <div key={idx} className="text-xs p-2 bg-amber-50 rounded mb-1 border border-amber-200">
                        <span className="font-medium">{issue.nome}</span>: {issue.problems?.join(', ')}
                      </div>
                    ))}
                    {diagnosticResults.issues.length > 10 && (
                      <p className="text-xs text-slate-500 mt-2">... e mais {diagnosticResults.issues.length - 10} problemas</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Deep Diagnostic Log */}
            {deepDiagnosticLog && (
              <div className="mt-4 p-4 bg-slate-900 rounded-lg text-white font-mono text-xs max-h-64 overflow-y-auto">
                <h4 className="text-amber-400 mb-2">Log Detalhado - {deepDiagnosticLog.employee_nome}</h4>
                {deepDiagnosticLog.log?.map((entry, idx) => (
                  <div key={idx} className={`py-1 ${entry.status === 'OK' ? 'text-green-400' : entry.status === 'ERROR' ? 'text-red-400' : 'text-yellow-400'}`}>
                    [{entry.status}] {entry.step}: {JSON.stringify(entry.data || entry.error)}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Raw Data Dialog */}
        <Dialog open={rawDataOpen} onOpenChange={setRawDataOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Database className="w-5 h-5 text-purple-600" />
                Dados RAW dos Funcionários
              </DialogTitle>
              <DialogDescription>
                Dados brutos retornados pela API para análise
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              {loadingRawData ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                </div>
              ) : (
                <pre className="text-xs bg-slate-900 text-green-400 p-4 rounded-lg overflow-x-auto">
                  {JSON.stringify(rawData, null, 2)}
                </pre>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRawDataOpen(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
